<template>
  <div class="ma-4">
    <v-snackbar
      v-model="msg"
      top
      :color="snackbarColor"
      :timeout="3000"
      elevation="4"
      class="rounded-lg"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">{{
          snackbarColor === "success" ? "mdi-check-circle" : "mdi-alert"
        }}</v-icon>
        {{ text }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small v-bind="attrs" @click="msg = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!--START INCIDENT TABLE-->
    <v-card class="rounded-lg">
      <v-app-bar flat dense color="grey lighten-4" class="rounded-t-lg px-3">
        <v-btn icon small class="mr-2 grey lighten-3" @click="$router.go(-1)">
          <v-icon>navigate_before</v-icon>
        </v-btn>

        <span class="text-h6 font-weight-medium grey--text text--darken-3">
          Incident Reports Graph
        </span>

        <v-spacer></v-spacer>
      </v-app-bar>

      <v-divider></v-divider>

      <v-card-text class="pa-6 grey lighten-5">
        <v-row dense @keydown.enter="searchIncidents">
          <v-col cols="12" md="4">
            <v-menu
              ref="startDateMenu"
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.startDate"
                  label="Start Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  background-color="white"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.startDate"
                @input="startDateMenu = false"
                color="primary"
                class="rounded-lg"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="4">
            <v-menu
              ref="endDateMenu"
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.endDate"
                  label="End Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  outlined
                  dense
                  hide-details
                  class="rounded-lg"
                  background-color="white"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filters.endDate"
                @input="endDateMenu = false"
                color="primary"
                class="rounded-lg"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="4">
            <v-select
              label="Select an Incident Type"
              :items="incidentTypes"
              item-text="Name"
              item-value="Name"
              v-model="filters.incidentType"
              clearable
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
            ></v-select>
          </v-col>

          <v-col cols="12" md="4">
            <v-select
              label="Select an Incident Category"
              :items="incidentCategories"
              item-text="Name"
              item-value="Name"
              v-model="filters.selectedIncidentCategory"
              clearable
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
            ></v-select>
          </v-col>

          <v-col cols="12" md="4">
            <v-select
              label="Select a Client"
              :items="filteredClients"
              item-text="Name"
              item-value="_id"
              v-model="filters.ClientID"
              clearable
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
            ></v-select>
          </v-col>

          <v-col cols="12" md="4">
            <v-select
              label="Select a Program"
              :items="units"
              item-text="Name"
              item-value="Name"
              v-model="filters.program"
              clearable
              outlined
              dense
              hide-details
              class="rounded-lg"
              background-color="white"
            ></v-select>
          </v-col>
        </v-row>

        <v-row class="mt-6">
          <v-col cols="12">
            <div class="d-flex justify-space-between align-center">
              <div>
                <v-btn
                  class="rounded-lg mr-4 primary"
                  :disabled="isLoading"
                  @click="searchIncidents()"
                  elevation="1"
                  large
                >
                  <v-icon left v-if="!isLoading">mdi-magnify</v-icon>
                  <v-progress-circular v-else left indeterminate size="20" color="white"></v-progress-circular>
                  {{ hasActiveFilters ? 'Search with Filters' : 'Search Incidents' }}
                </v-btn>
                <v-btn
                  color="secondary"
                  class="rounded-lg"
                  :disabled="isLoading"
                  @click="clearFilters()"
                  elevation="1"
                  large
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Clear Filters
                </v-btn>
              </div>
              <div>
                <v-btn
                  color="primary"
                  class="rounded-lg mr-4"
                  :disabled="isLoading"
                  @click="generateIncidentTypeChartData()"
                  elevation="1"
                  large
                >
                  <v-icon left>mdi-chart-bar</v-icon>
                  Incident Trends Chart
                </v-btn>
                <v-btn
                  color="primary"
                  class="rounded-lg"
                  :disabled="isLoading"
                  @click="generateTimeDifferenceChartData()"
                  elevation="1"
                  large
                >
                  <v-icon left>mdi-clock</v-icon>
                  Time Difference Chart
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Data Table -->
      <v-card-text class="pa-6">
        <div v-if="!isLoading && incidents.length > 0" class="mb-4">
          <v-alert
            type="info"
            dense
            outlined
            class="rounded-lg"
          >
            <v-icon left>mdi-information</v-icon>
            Found {{ itemCount }} incident{{ itemCount !== 1 ? 's' : '' }} matching your criteria
          </v-alert>
        </div>
        
        <div v-else-if="!isLoading && incidents.length === 0" class="mb-4">
          <v-alert
            type="warning"
            dense
            outlined
            class="rounded-lg"
          >
            <v-icon left>mdi-alert</v-icon>
            No incidents found matching your criteria. Try adjusting your filters.
          </v-alert>
        </div>

        <v-data-table
          :headers="state === 'Utah' ? headers : nevadaHeaders"
          :items="incidents"
          class="elevation-1 rounded-lg"
          :loading="isLoading"
          loading-text="Loading Incident Reports"
          :items-per-page="100"
          :page="currentPage"
          :pageCount="pageCount"
          :server-items-length="itemCount"
          :options.sync="options"
          :footer-props="{
            'items-per-page-options': [5, 10, 20, 50, 100],
            showFirstLastPage: true,
            'items-per-page-text': 'Rows per page:',
            class: 'rounded-lg',
          }"
        >
          <template v-slot:item.select="{ item }">
            <v-btn
              x-small
              color="primary"
              @click="viewIncidentReport(item)"
              class="px-3"
            >
              <v-icon left x-small>mdi-eye</v-icon>
              View
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <!--END INCIDENT TABLE-->

    <!--START INCIDENT GRAPH DIALOG-->
    <v-dialog v-model="isTimeDifferenceChartDialogVisible" max-width="80%" class="rounded-lg">
      <v-card class="rounded-lg">
        <v-card-title class="primary white--text">
          <span class="text-h6">Viewing Time Difference Chart</span>
        </v-card-title>
        <v-card-text class="pt-6">
          <LineChart
            :loading="isLoading"
            :chart-data="timeDifferenceChartData"
            chart-id="timeDifferenceChart"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            text
            @click="isTimeDifferenceChartDialogVisible = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Incident Type Chart Dialog -->
    <v-dialog v-model="isIncidentTypeChartDialogVisible" max-width="80%" class="rounded-lg">
      <v-card class="rounded-lg">
        <v-card-title class="primary white--text">
          <span class="text-h6">{{ isComparisonMode ? 'Incident Comparison: ' + getPeriod1Label() + ' vs ' + getPeriod2Label() : 'Incident Trends Over Time' }}</span>
        </v-card-title>
        <v-card-text class="pt-6">
          <!-- Time Period Comparison Controls -->
          <v-expansion-panels v-model="comparisonPanel" class="mb-4">
            <v-expansion-panel class="comparison-panel">
              <v-expansion-panel-header>
                <template v-slot:default>
                  <div class="d-flex align-center justify-center w-100">
                    <v-icon left class="mr-2 white--text">mdi-chart-timeline-variant</v-icon>
                    <span class="text-h6 font-weight-medium white--text">View Time Period Comparison</span>
                  </div>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row dense>
                  <v-col cols="12" md="3">
                    <v-select
                      label="Comparison Type"
                      :items="comparisonTypes"
                      v-model="selectedComparisonType"
                      outlined
                      dense
                      @change="updateComparisonPeriods()"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-menu
                      ref="period1StartMenu"
                      v-model="period1StartMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="period1.startDate"
                          label="Period 1 Start"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          dense
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="period1.startDate"
                        @input="period1StartMenu = false"
                        color="primary"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-menu
                      ref="period1EndMenu"
                      v-model="period1EndMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="period1.endDate"
                          label="Period 1 End"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          dense
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="period1.endDate"
                        @input="period1EndMenu = false"
                        color="primary"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-chip
                      color="primary"
                      text-color="white"
                      class="ma-2"
                    >
                      {{ getPeriod1Label() }}
                    </v-chip>
                  </v-col>
                </v-row>
                <v-row dense>
                  <v-col cols="12" md="3">
                    <v-divider class="my-2"></v-divider>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-menu
                      ref="period2StartMenu"
                      v-model="period2StartMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="period2.startDate"
                          label="Period 2 Start"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          dense
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="period2.startDate"
                        @input="period2StartMenu = false"
                        color="primary"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-menu
                      ref="period2EndMenu"
                      v-model="period2EndMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="period2.endDate"
                          label="Period 2 End"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          dense
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="period2.endDate"
                        @input="period2EndMenu = false"
                        color="primary"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-chip
                      color="secondary"
                      text-color="white"
                      class="ma-2"
                    >
                      {{ getPeriod2Label() }}
                    </v-chip>
                  </v-col>
                </v-row>
                <v-row class="mt-4">
                  <v-col cols="12" class="text-center">
                    <v-btn
                      color="success"
                      class="rounded-lg"
                      :disabled="isLoading"
                      @click="generateComparisonChartData()"
                      elevation="1"
                    >
                      <v-icon left>mdi-chart-timeline-variant</v-icon>
                      Generate Comparison
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Summary Section -->
          <div v-if="incidentTypeChartData && incidentTypeChartData.datasets" class="mb-4">
            <v-row>
              <v-col cols="12" md="3">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h6 primary--text">{{ getTotalIncidents() }}</div>
                  <div class="text-caption">Total Incidents</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h6 primary--text">{{ getDateRange() }}</div>
                  <div class="text-caption">Date Range</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h6 primary--text">{{ getMostCommonType() }}</div>
                  <div class="text-caption">Most Common Type</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="3" v-if="isComparisonMode">
                <v-card outlined class="pa-3 text-center">
                  <div class="text-h6" :class="getTrendColor()">{{ getTrendAnalysis() }}</div>
                  <div class="text-caption">Trend Analysis</div>
                </v-card>
              </v-col>
            </v-row>
          </div>
          
          <BarChart
            :loading="isLoading"
            :chart-data="incidentTypeChartData"
            chart-id="incidentTypeChart"
            :chart-options="incidentChartOptions"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            text
            @click="isIncidentTypeChartDialogVisible = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!--END INCIDENT GRAPH DIALOG-->
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import LineChart from '../../components/shared/LineChart';
import BarChart from '../../components/shared/BarChart';
import {STATUS_ARRAY} from '@/enums/UserStatusEnum';
import DateTimeUtil from "../../../utils/date-time.util";

const url = require("../../client_config/config").url;

export default {
  components: {
    LineChart,
    BarChart
  },
  data: () => ({
    headers: [
      {
        text: "Client",
        value: "ClientInfo.Name",
        sortable: true
      },
      {
        text: "Program",
        value: "Program",
        sortable: true
      },
      {
        text: "Incident Type",
        value: "Type",
        sortable: true
      },
      {
        text: "Notified",
        value: "ReportSubmitted"
      },
      {
        text: 'Incident Date and Approximate Time',
        align: 'start',
        value: 'IncidentDateTime',
        sortable: true
      },
      {
        text: 'Incident Discovered Date and Time',
        align: 'start',
        value: 'IncidentDiscovered',
      },
      {
        text: "Time Difference",
        value: "TimeDifference"
      },
      {
        text: '',
        value: "select",
        sortable: false
      }
    ],
    nevadaHeaders: [
      {
        text: "Client",
        value: "ClientInfo.Name",
        sortable: true
      },
      {
        text: "Program",
        value: "Program",
        sortable: true
      },
      {
        text: 'Incident Date',
        align: 'start',
        value: 'IncidentDateTime',
        sortable: true
      },
      {
        text: 'Incident Notification Date',
        align: 'start',
        value: 'ReportSubmittedDate',
      },
      {
        text: "Time Difference",
        value: "TimeDifference",
        sortable: false
      },
      {
        text: "Incident Type",
        value: "Type",
        sortable: true
      },
      {
        text: "Notified",
        value: "ReportSubmitted"
      },
      {
        text: '',
        value: "select",
        sortable: false
      }
    ],
    date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
    page: 'Incidents',
    category: ["Behavioral", "Medical", "Hospital", "Injury"],
    clients: [],
    incidents: [],
    units: [],
    startDate: '',
    endDate: '',
    ClientID: '',
    state: '',
    graphStartDateCalendarMenu: false,
    graphEndDateCalendarMenu: false,
    graphStartDate: '',
    graphEndDate: '',
    report: '',
    clientName: '',
    isLoading: false,
    msg: false,
    snackbarColor: "success",
    text: '',
    program: '',
    states: ['Utah', 'Nevada'],
    userState: localStorage.getItem('userState'),
    userRole: localStorage.getItem('userRole'),
    isUtahIncidentReports: true,
    isTimeDifferenceChartDialogVisible: false,
    isIncidentTypeChartDialogVisible: false,
    // isIncidentReportViewDialogVisible: false,
    // isIncidentReportGraphDialogVisible: false,
    isLoadingIncidentsGraphData: false,
    timeDifferenceChartData: null,
    incidentTypeChartData: [],
    chartOptions: [],
    incidentChartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: 'Incident Trends Over Time',
          font: {
            size: 16,
            weight: 'bold'
          }
        },
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: function(context) {
              return 'Date: ' + context[0].label;
            },
            label: function(context) {
              return context.dataset.label + ': ' + context.parsed.y + ' incident(s)';
            }
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Date',
            font: {
              weight: 'bold'
            }
          },
          grid: {
            display: true,
            color: 'rgba(0,0,0,0.1)'
          }
        },
        y: {
          title: {
            display: true,
            text: 'Number of Incidents',
            font: {
              weight: 'bold'
            }
          },
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            precision: 0
          },
          grid: {
            display: true,
            color: 'rgba(0,0,0,0.1)'
          }
        }
      },
      interaction: {
        mode: 'index',
        intersect: false
      }
    },
    selectedUtahIncident: {},
    selectedNevadaIncident: {},
    nevadaIRTypes: ["Behavioral", "Medical", "Hospital", "Injury", "Allegations", "Property Damage"],
    utahIRTypes: ["Behavioral", "Medical", "Hospital", "Injury", "Property Damage"],
    incidentCategories: ["Abuse/Neglect/Exploitation", "Aspiration/Choking", "Behavior Intervention", "Compromised Environment", "Drug/Alcohol", "Elopement", "Fatality", "Hospital", "Injury", "Medication Errors", "Missing Person", "Physical Aggression", "Property Destruction", "Restrained", "Seizure", "Suicide Attempt/Threat", "Theft", "Other (describe)"],
    incidentTypes: [],
    selectedIncidentCategory: [],
    incidentType: '',
    dateRange: ['Last Year', 'Previous Month', 'Last 7 days'],
    selectedDateRange: '',
    tabs: null,
    currentPage: 1,
    pageCount: 1,
    itemCount: 0,
    options: {},
    clientStatus: "",
    clientStatuses: STATUS_ARRAY,
    filteredClients: [],
    startDateMenu: false,
    endDateMenu: false,
    filters: {
      startDate: '',
      endDate: '',
      incidentType: '',
      selectedIncidentCategory: '',
      program: '',
      clientStatus: '',
      ClientID: '',
    },
    comparisonTypes: [
      { text: 'Previous Month vs Current Month', value: 'previousMonthVsCurrentMonth' },
      { text: 'Year Over Year (Same Date Range)', value: 'yearOverYear' },
      { text: 'Last Year vs Previous Month', value: 'lastYearVsPreviousMonth' },
      { text: 'Last Year vs Last 7 days', value: 'lastYearVsLast7Days' },
      { text: 'Previous Month vs Last 7 days', value: 'previousMonthVsLast7Days' },
      { text: 'Last Year vs Current Month', value: 'lastYearVsCurrentMonth' },
      { text: 'Last 7 days vs Current Month', value: 'last7DaysVsCurrentMonth' }
    ],
    selectedComparisonType: 'previousMonthVsCurrentMonth',
    period1: {
      startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      endDate: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
    },
    period2: {
      startDate: moment().startOf('month').format('YYYY-MM-DD'),
      endDate: moment().endOf('month').format('YYYY-MM-DD')
    },
    period1StartMenu: false,
    period1EndMenu: false,
    period2StartMenu: false,
    period2EndMenu: false,
    comparisonPanel: null
  }),

  computed: {
    hasActiveFilters() {
      return this.filters.startDate || 
             this.filters.endDate || 
             this.filters.incidentType || 
             this.filters.selectedIncidentCategory || 
             this.filters.program || 
             this.filters.clientStatus || 
             this.filters.ClientID;
    },
    isComparisonMode() {
      return this.selectedComparisonType !== 'lastYearVsPreviousMonth';
    }
  },

  watch: {
    options: {
      handler() {
        this.searchIncidents();
      },
      deep: true
    },
    clientStatus: function (val) {
      if (val === '' || val == null) {
        this.filteredClients = this.clients;
      } else {
        this.filteredClients = this.clients.filter((client) => client.Status === val);
      }
      this.filters.clientStatus = val;
    },
    filters: {
      handler() {
        // Ensure dates are always in correct format
        if (this.filters.startDate) {
          this.filters.startDate = moment(this.filters.startDate).format('YYYY-MM-DD');
        }
        if (this.filters.endDate) {
          this.filters.endDate = moment(this.filters.endDate).format('YYYY-MM-DD');
        }
        this.setFilters();
      },
      deep: true
    }
  },

  created() {
    this.incidentTypes = this.userState === 'Utah' ? this.utahIRTypes : this.nevadaIRTypes;
    this.getClients();
    this.getPrograms();
    this.getFilters();
    // Perform initial search to load data
    this.$nextTick(() => {
      this.searchIncidents();
    });
  },

  methods: {
    initialize() {
      this.clients = [];
    },

    async getClients() {
      let params = {};
      let endpoint = `${url}/clients`;

      const companyIds = localStorage.getItem('userCompanies');

      params['Company:in'] = JSON.parse(companyIds);
      params['State'] = this.userState;

      await axios.get(endpoint, {params})
        .then((res) => {
          this.clients = res.data.clients;
          this.filteredClients = this.clients;
        });
    },

    async getPrograms() {
      await axios.get(`${url}/units/state/${this.userState}`)
        .then((res) => {
          this.units = res.data.units;
        });
    },

    getIncidents() {
      this.incidents = [];

      axios.get(`${url}/incidents`).then(async res => {
        this.incidents = res.data.incidents;
        this.isLoading = false;
      });
    },

    close() {
      this.seditInfo = false;
      this.passdialog = false;
      this.chgimg = false;
    },

    async searchIncidents() {
      this.isLoading = true;
      this.filters.program = this.filters.program || '';
      this.filters.startDate = this.filters.startDate || moment().startOf('day').format('YYYY-MM-DD');
      this.filters.endDate = this.filters.endDate || moment().endOf('day').format('YYYY-MM-DD');
      this.filters.ClientID = this.filters.ClientID || '';
      this.filters.incidentType = this.filters.incidentType || '';
      this.filters.incidentCategory = this.filters.selectedIncidentCategory || [];
      this.state = localStorage.getItem('userState');
      this.incidents = [];

      if (this.state === 'Utah') {
        this.incidents = await this.getUtahIncidentReports();
      } else if (this.state === 'Nevada') {
        this.incidents = await this.getNevadaIncidentReports();
      }

      this.isLoading = false;
    },

    async getUtahIncidentReports() {
      const urlEndpoint = `${url}/incidents/reports`;

      let {page, itemsPerPage, sortBy, sortDesc} = this.options;
      page = page || 1;
      itemsPerPage = itemsPerPage || 100;
      this.currentPage = page;

      this.program = this.program || '';
      this.ClientID = this.ClientID || '';
      this.incidentType = this.incidentType || '';

      const params = {
        page: page,
        limit: itemsPerPage,
        program: this.filters.program || '',
        startDate: DateTimeUtil.toUTCISOString(this.filters.startDate),
        endDate: DateTimeUtil.toUTCISOString(this.filters.endDate),
        clientID: this.filters.ClientID || '',
        state: this.state || '',
        clientStatus: this.filters.clientStatus || '',
        sortField: (sortBy && sortBy.length) ? sortBy[0] : '_id',
        sortDirection: (sortDesc && sortDesc.length && sortDesc[0]) ? 'desc' : 'asc',
        incidentType: this.filters.incidentType || '',
        incidentCategory: this.filters.selectedIncidentCategory || ''
      };

      const incidentsResponse = await axios.get(urlEndpoint, {params});

      console.log({incidentsResponse});

      const {docs, totalPages, totalDocs} = incidentsResponse.data;
      this.pageCount = totalPages;
      this.itemCount = totalDocs;
      let incidentReports = [];

      if (docs) {
        incidentReports = this.mapIncidents(docs);
      }

      return incidentReports;
    },

    async getNevadaIncidentReports() {
      let {page, itemsPerPage, sortBy, sortDesc} = this.options;
      page = page || 1;
      itemsPerPage = itemsPerPage || 100;
      this.currentPage = page;

      this.filters.program = this.filters.program || '';
      this.filters.ClientID = this.filters.ClientID || '';
      this.filters.incidentType = this.filters.incidentType || '';

      const params = {
        page: page,
        limit: itemsPerPage,
        program: this.filters.program || '',
        startDate: DateTimeUtil.toUTCISOString(this.filters.startDate),
        endDate: DateTimeUtil.toUTCISOString(this.filters.endDate),
        clientId: this.filters.ClientID || '',
        state: this.state || '',
        clientStatus: this.filters.clientStatus || '',
        sortField: (sortBy && sortBy.length) ? sortBy[0] : '_id',
        sortDirection: (sortDesc && sortDesc.length && sortDesc[0]) ? 'desc' : 'asc',
        incidentType: this.filters.incidentType
      };


      const incidentsResponse = await axios.get(`${url}/nevadaIncidents`, {params});
      const {docs, totalPages, totalDocs} = incidentsResponse.data.nevadaIncidents;
      this.pageCount = totalPages;
      this.itemCount = totalDocs;
      let incidentReports = [];

      if (docs) {
        incidentReports = this.mapIncidents(docs);
      }

      return incidentReports;
    },

    async viewIncidentsGraph() {
      if (this.isLoadingIncidentsGraphData) {
        alert('Still loading incidents graph data. Please wait...');
        return;
      }

      if (this.filters.startDate && this.filters.endDate) {
        this.startDate = DateTimeUtil.toStartOfDay(this.filters.startDate);
        this.endDate = DateTimeUtil.toEndOfDay(this.filters.endDate);

        await this.searchIncidents();
      }

      this.isLoadingIncidentsGraphData = true;
      // this.timeDifferenceChartData = this.generateTimeDifferenceChartData(this.incidents);
      this.incidentTypeChartData = this.generateIncidentTypeChartData(this.incidents);
      this.isIncidentReportGraphDialogVisible = true;
      this.isLoadingIncidentsGraphData = false;
    },

    printGraph() {
      let id = this.tabs === 0 ? 'timeDifferenceChart' : 'incidentTypeChart';
      let chartName = this.tabs === 0 ? 'Time Difference Chart' : 'Incident Type Chart';
      let timeDifferenceChartElement = document.getElementById(id).toDataURL();
      let html = `<html lang="en"><head><title></title></head><body><br><br><br><br><br><br><div align="center"><h3>${chartName}</h3><img src=${timeDifferenceChartElement} alt="" style="width: 75%"></div></body>`;

      let printWindow = window.open('', 'Print-Preview', 'height=auto,width=auto');
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      setTimeout(() => printWindow.print(), 500);
    },

    async generateIncidentTypeChartData() {
      if (this.filters.startDate && this.filters.endDate) {
        this.startDate = DateTimeUtil.toStartOfDay(this.filters.startDate);
        this.endDate = DateTimeUtil.toEndOfDay(this.filters.endDate);

        await this.searchIncidents();
      }

      const incidents = this.incidents || [];
      this.isIncidentTypeChartDialogVisible = true;

      console.log({incidentsBar: incidents});

      // Debug: Log the first few incidents to see their date structure
      if (incidents.length > 0) {
        console.log('Sample incident date fields:', {
          IncidentDateTime: incidents[0].IncidentDateTime,
          IncidentDateFormatted: incidents[0].IncidentDateFormatted,
          Type: incidents[0].Type
        });
      }

      // Group incidents by date and type
      const dateTypeMap = {};
      const allDates = new Set();
      const allTypes = new Set();

      incidents.forEach(incident => {
        // Use IncidentDateTime if available, otherwise fall back to IncidentDateFormatted
        let date = null;
        if (incident.IncidentDateTime) {
          // Extract just the date part from the datetime string
          const dateTimeStr = incident.IncidentDateTime;
          if (dateTimeStr.includes(' ')) {
            date = dateTimeStr.split(' ')[0]; // Get the date part before the space
          } else {
            date = dateTimeStr;
          }
        } else if (incident.IncidentDateFormatted) {
          date = incident.IncidentDateFormatted;
        }
        
        if (date && incident.Type) {
          const type = incident.Type;
          
          allDates.add(date);
          allTypes.add(type);
          
          if (!dateTypeMap[date]) {
            dateTypeMap[date] = {};
          }
          
          if (!dateTypeMap[date][type]) {
            dateTypeMap[date][type] = 0;
          }
          
          dateTypeMap[date][type]++;
        }
      });

      // Sort dates chronologically
      const sortedDates = Array.from(allDates).sort();
      const sortedTypes = Array.from(allTypes).sort();

      console.log('Sorted dates:', sortedDates);
      console.log('Sorted types:', sortedTypes);
      console.log('Date type map:', dateTypeMap);
      console.log('All collected dates:', Array.from(allDates));

      // Create datasets for each incident type
      const datasets = [];
      const chartColors = [
        'rgb(255, 99, 132)',   // Red
        'rgb(54, 162, 235)',   // Blue
        'rgb(255, 205, 86)',   // Yellow
        'rgb(75, 192, 192)',   // Teal
        'rgb(153, 102, 255)',  // Purple
        'rgb(255, 159, 64)',   // Orange
        'rgb(201, 203, 207)'   // Gray
      ];

      sortedTypes.forEach((type, index) => {
        const data = sortedDates.map(date => {
          return dateTypeMap[date] && dateTypeMap[date][type] ? dateTypeMap[date][type] : 0;
        });

        const dataset = {
          label: type,
          data: data,
          borderColor: chartColors[index % chartColors.length],
          backgroundColor: chartColors[index % chartColors.length],
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false
        };

        datasets.push(dataset);
      });

      // Format dates for better display
      const formattedLabels = sortedDates.map(date => {
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: '2-digit'
        });
      });

      this.incidentTypeChartData = {
        labels: formattedLabels,
        datasets: datasets
      };

      console.log('Final chart data:', this.incidentTypeChartData);
    },

    async generateComparisonChartData() {
      this.isIncidentTypeChartDialogVisible = true;
      
      // Fetch data for both periods
      const period1Data = await this.fetchIncidentsForPeriod(this.period1.startDate, this.period1.endDate);
      const period2Data = await this.fetchIncidentsForPeriod(this.period2.startDate, this.period2.endDate);
      
      console.log('Period 1 data:', period1Data);
      console.log('Period 2 data:', period2Data);
      
      // Process data for comparison chart
      const comparisonData = this.processComparisonData(period1Data, period2Data);
      
      this.incidentTypeChartData = comparisonData;
    },

    async fetchIncidentsForPeriod(startDate, endDate) {
      const params = {
        page: 1,
        limit: 1000, // Get all incidents for the period
        program: this.filters.program || '',
        startDate: DateTimeUtil.toUTCISOString(startDate),
        endDate: DateTimeUtil.toUTCISOString(endDate),
        clientID: this.filters.ClientID || '',
        state: this.state || '',
        clientStatus: this.filters.clientStatus || '',
        sortField: '_id',
        sortDirection: 'asc',
        incidentType: this.filters.incidentType || '',
        incidentCategory: this.filters.selectedIncidentCategory || ''
      };

      try {
        const response = await axios.get(`${url}/incidents/reports`, { params });
        return response.data.docs || [];
      } catch (error) {
        console.error('Error fetching incidents for period:', error);
        return [];
      }
    },

    processComparisonData(period1Data, period2Data) {
      // Get all unique incident types from both periods
      const allTypes = new Set();
      [...period1Data, ...period2Data].forEach(incident => {
        if (incident.Type) {
          allTypes.add(incident.Type);
        }
      });

      const sortedTypes = Array.from(allTypes).sort();
      const chartColors = [
        'rgb(255, 99, 132)',   // Red
        'rgb(54, 162, 235)',   // Blue
        'rgb(255, 205, 86)',   // Yellow
        'rgb(75, 192, 192)',   // Teal
        'rgb(153, 102, 255)',  // Purple
        'rgb(255, 159, 64)',   // Orange
        'rgb(201, 203, 207)'   // Gray
      ];

      const datasets = [];
      
      sortedTypes.forEach((type, index) => {
        // Count incidents for this type in each period
        const period1Count = period1Data.filter(incident => incident.Type === type).length;
        const period2Count = period2Data.filter(incident => incident.Type === type).length;
        
        const color = chartColors[index % chartColors.length];
        
        // Create dataset for this incident type
        const dataset = {
          label: type,
          data: [period1Count, period2Count],
          backgroundColor: [color, color],
          borderColor: [color, color],
          borderWidth: 1,
          borderRadius: 4
        };
        
        datasets.push(dataset);
      });

      return {
        labels: [this.getPeriod1Label(), this.getPeriod2Label()],
        datasets: datasets
      };
    },


    async generateTimeDifferenceChartData() {
      if (this.filters.startDate && this.filters.endDate) {
        this.startDate = DateTimeUtil.toStartOfDay(this.filters.startDate);
        this.endDate = DateTimeUtil.toEndOfDay(this.filters.endDate);

        await this.searchIncidents();
      }

      const incidents = this.incidents || [];
      const labels = [];
      const timeDifferencesInHours = [];

      incidents.forEach(incident => {
        labels.push(incident.IncidentDateTime);
        timeDifferencesInHours.push(Number(incident.TimeDifferenceInHours.toFixed(2)));
      });

      this.timeDifferenceChartData = {
        labels: labels,
        datasets: [
          {
            label: 'Time Difference In Hours (Incident Date and Approximate Time vs Incident Discovered Date and Time)',
            data: timeDifferencesInHours,
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgb(255, 99, 132)',
            pointStyle: 'circle',
            pointRadius: 5,
            pointHoverRadius: 10
          }
        ]
      };

      // Open the dialog that displays the chart.
      this.isTimeDifferenceChartDialogVisible = true;
    },


    getRandomColor(usedColors) {
      const chartColors = [
        'rgb(255, 99, 132)',
        'rgb(255, 159, 64)',
        'rgb(255, 205, 86)',
        'rgb(75, 192, 192)',
        'rgb(54, 162, 235)',
        'rgb(153, 102, 255)',
        'rgb(201, 203, 207)'
      ];
      let color = '';

      // Get colors from chart colors if not used
      for (let chartColor of chartColors) {
        if (usedColors && Array.isArray(usedColors) && usedColors.indexOf(chartColor) < 0) {
          color = chartColor;
          break;
        }
      }

      // Get random colors if all the chart colors above are already in use
      if (!color) {
        color = '#' + Math.floor(Math.random() * 16777215).toString(16);

        if (usedColors && Array.isArray(usedColors) && usedColors.indexOf(color) > -1) {
          color = this.getRandomColor(usedColors);
        }
      }

      return color;
    },

    viewIncidentReport(incidentReport) {
      // this.isIncidentReportViewDialogVisible = true;

      if (this.userState === 'Utah') {
        this.$router.push({
          path: `/incidents/${this.clientID}/update-incident/${incidentReport._id}`,
          query: {newIncident: 'false', clientNav: 'false'}
        });

      } else if (this.userState === 'Nevada') {
        this.$router.push({
          path: `/nvIncidents/${this.clientID}/update-incident/${incidentReport._id}`,
          query: {newIncident: 'false', clientNav: 'false'}
        });
      }
    },

    mapIncidents(incidents) {

      console.log({incidents});

      return incidents.map(incident => {

        incident.TimeDifference = 0;
        incident.TimeDifferenceInHours = 0;
        const rsd = moment(incident.IncidentDiscovered);

        if (rsd.isValid()) {
          const difference = moment(incident.IncidentDiscovered).diff(moment(incident.IncidentDateTime));
          const duration = moment.duration(difference);

          incident.TimeDifference = duration.asHours() === 0 ? duration.asMinutes().toFixed(2) + ' minutes' : duration.asHours().toFixed(2) + ' hours';
          incident.TimeDifferenceInHours = duration.asHours();
          incident.IncidentDiscovered = rsd.format('YYYY-MM-DD hh:mm:A');
        } else {
          incident.IncidentDiscovered = 0;
        }
        let incidentDateTime = null;

        if (incident.IncidentDateTime) {
          incidentDateTime = new moment(incident?.IncidentDateTime?.trim());
        } else {
          incidentDateTime = new moment(incident?.IncidentDate?.trim() + ' ' + incident?.IncidentTime?.trim());
        }

        if (!incidentDateTime.isValid()) {
          incidentDateTime = new moment(incident?.IncidentDate.trim());
        }

        incident.IncidentDateFormatted = moment(incident.IncidentDate).format('YYYY-MM-DD');
        incident.IncidentDateTime = incidentDateTime.format('YYYY-MM-DD hh:mm:A');

        return incident;
      });
    },

    getFilters() {
      let filters = localStorage.getItem('incidentReportGraphFilters');
      if (filters) {
        this.filters = JSON.parse(filters);
        this.clientStatus = this.filters.clientStatus;
        
        // Ensure dates are in correct format (YYYY-MM-DD only)
        if (this.filters.startDate) {
          this.filters.startDate = moment(this.filters.startDate).format('YYYY-MM-DD');
        }
        if (this.filters.endDate) {
          this.filters.endDate = moment(this.filters.endDate).format('YYYY-MM-DD');
        }
      }
    },

    setFilters() {
      localStorage.setItem('incidentReportGraphFilters', JSON.stringify(this.filters));
    },

    clearFilters() {
      this.filters = {
        startDate: '',
        endDate: '',
        incidentType: '',
        selectedIncidentCategory: '',
        program: '',
        clientStatus: '',
        ClientID: '',
      };
      this.clientStatus = '';
      this.startDateMenu = false;
      this.endDateMenu = false;
      localStorage.removeItem('incidentReportGraphFilters');
      this.searchIncidents();
    },

    getTotalIncidents() {
      if (!this.incidentTypeChartData || !this.incidentTypeChartData.datasets) {
        return 0;
      }
      
      let total = 0;
      this.incidentTypeChartData.datasets.forEach(dataset => {
        total += dataset.data.reduce((sum, count) => sum + count, 0);
      });
      
      return total;
    },

    getDateRange() {
      if (!this.incidentTypeChartData || !this.incidentTypeChartData.labels || this.incidentTypeChartData.labels.length === 0) {
        return 'N/A';
      }
      
      const labels = this.incidentTypeChartData.labels;
      const startDate = labels[0];
      const endDate = labels[labels.length - 1];
      
      return `${startDate} - ${endDate}`;
    },

    getMostCommonType() {
      if (!this.incidentTypeChartData || !this.incidentTypeChartData.datasets || this.incidentTypeChartData.datasets.length === 0) {
        return 'N/A';
      }
      
      let maxTotal = 0;
      let mostCommonType = 'N/A';
      
      this.incidentTypeChartData.datasets.forEach(dataset => {
        const total = dataset.data.reduce((sum, count) => sum + count, 0);
        if (total > maxTotal) {
          maxTotal = total;
          mostCommonType = dataset.label;
        }
      });
      
      return mostCommonType;
    },

               updateComparisonPeriods() {
        const selectedType = this.selectedComparisonType;
        const now = moment();

        if (selectedType === 'yearOverYear') {
          // For year-over-year, use the current date range but go back one year
          const currentStart = moment(this.filters.startDate || now.startOf('month'));
          const currentEnd = moment(this.filters.endDate || now.endOf('month'));
          
          this.period1.startDate = currentStart.subtract(1, 'year').format('YYYY-MM-DD');
          this.period1.endDate = currentEnd.subtract(1, 'year').format('YYYY-MM-DD');
          this.period2.startDate = currentStart.add(1, 'year').format('YYYY-MM-DD');
          this.period2.endDate = currentEnd.add(1, 'year').format('YYYY-MM-DD');
        } else if (selectedType === 'lastYearVsPreviousMonth') {
          this.period1.startDate = moment().subtract(1, 'year').startOf('month').format('YYYY-MM-DD');
          this.period1.endDate = moment().subtract(1, 'year').endOf('month').format('YYYY-MM-DD');
          this.period2.startDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.period2.endDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        } else if (selectedType === 'lastYearVsLast7Days') {
          this.period1.startDate = moment().subtract(1, 'year').subtract(7, 'days').startOf('day').format('YYYY-MM-DD');
          this.period1.endDate = moment().subtract(1, 'year').subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
          this.period2.startDate = moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD');
          this.period2.endDate = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
        } else if (selectedType === 'previousMonthVsLast7Days') {
          this.period1.startDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.period1.endDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
          this.period2.startDate = moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD');
          this.period2.endDate = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
        } else if (selectedType === 'lastYearVsCurrentMonth') {
          this.period1.startDate = moment().subtract(1, 'year').startOf('month').format('YYYY-MM-DD');
          this.period1.endDate = moment().subtract(1, 'year').endOf('month').format('YYYY-MM-DD');
          this.period2.startDate = now.startOf('month').format('YYYY-MM-DD');
          this.period2.endDate = now.endOf('month').format('YYYY-MM-DD');
        } else if (selectedType === 'previousMonthVsCurrentMonth') {
          this.period1.startDate = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          this.period1.endDate = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
          this.period2.startDate = now.startOf('month').format('YYYY-MM-DD');
          this.period2.endDate = now.endOf('month').format('YYYY-MM-DD');
        } else if (selectedType === 'last7DaysVsCurrentMonth') {
          this.period1.startDate = moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD');
          this.period1.endDate = moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
          this.period2.startDate = now.startOf('month').format('YYYY-MM-DD');
          this.period2.endDate = now.endOf('month').format('YYYY-MM-DD');
        }
      },

    getPeriod1Label() {
      const start = moment(this.period1.startDate);
      const end = moment(this.period1.endDate);
      if (start.isValid() && end.isValid()) {
        return `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`;
      }
      return 'N/A';
    },

    getPeriod2Label() {
      const start = moment(this.period2.startDate);
      const end = moment(this.period2.endDate);
      if (start.isValid() && end.isValid()) {
        return `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`;
      }
      return 'N/A';
    },

    getTrendColor() {
      if (!this.incidentTypeChartData || !this.incidentTypeChartData.datasets || this.incidentTypeChartData.datasets.length === 0) {
        return '';
      }

      const period1Total = this.incidentTypeChartData.datasets[0].data.reduce((sum, count) => sum + count, 0);
      const period2Total = this.incidentTypeChartData.datasets[0].data.reduce((sum, count) => sum + count, 0);

      if (period1Total === 0 && period2Total === 0) {
        return 'grey--text';
      }

      if (period1Total > period2Total) {
        return 'success--text';
      } else if (period1Total < period2Total) {
        return 'error--text';
      } else {
        return 'primary--text';
      }
    },

    getTrendAnalysis() {
      if (!this.incidentTypeChartData || !this.incidentTypeChartData.datasets || this.incidentTypeChartData.datasets.length === 0) {
        return 'N/A';
      }

      const period1Total = this.incidentTypeChartData.datasets[0].data.reduce((sum, count) => sum + count, 0);
      const period2Total = this.incidentTypeChartData.datasets[0].data.reduce((sum, count) => sum + count, 0);

      if (period1Total === 0 && period2Total === 0) {
        return 'No incidents recorded in either period.';
      }

      if (period1Total > period2Total) {
        return 'Incidents are improving over time.';
      } else if (period1Total < period2Total) {
        return 'Incidents are getting worse over time.';
      } else {
        return 'Incidents are staying the same over time.';
      }
    }
  }
}
</script>

<style scoped>
.v-data-table {
  border: 1px solid #e0e0e0;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table ::v-deep tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.v-text-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.v-select ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.rounded-t-lg {
  border-top-left-radius: 8px !important;
  border-top-right-radius: 8px !important;
}

.comparison-panel {
  border: none !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15) !important;
  background: #f8f9ff !important;
  overflow: hidden !important;
}

.comparison-panel .v-expansion-panel-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  color: white !important;
  border-radius: 10px 10px 0 0 !important;
  min-height: 60px !important;
}

.comparison-panel .v-expansion-panel-header .v-expansion-panel-header__icon {
  color: white !important;
}

.comparison-panel .v-expansion-panel-header .v-expansion-panel-header__icon .v-icon {
  color: white !important;
  filter: brightness(1.5) !important;
}

.comparison-panel .v-expansion-panel-header:hover {
  background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%) !important;
}

.comparison-panel .v-expansion-panel-content {
  background: white !important;
  border-radius: 0 0 10px 10px !important;
  padding: 20px !important;
}
</style>
